import { Button } from '@ps/isp-web-component-library';
import type { MarketId, MarketView, SubMarketGroupType } from '@ps/isp-web-services-types';
import type { EventPageTabEntities } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import { isEmpty, noop } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import Redirect from '@swf/components/Redirect/Redirect';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import { MarketTemplatePanel, Message } from '@swf/sports/components';
import LiveDataWarning from '@swf/sports/components/LiveDataWarning/LiveDataWarning';
import MarketGroupTabs from '@swf/sports/components/MarketGroupTabs/MarketGroupTabs';
import { Search } from '@swf/sports/components/Search/Search';
import { PopularBetBuilders } from '@swf/sports/features';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';
import { marketTemplateTranslationMap } from '@swf/sports/utils/marketTemplateTranslationMap';

import { useSportsEventPageWidget } from '../../../../model';
import type { ClientMarketGroup, MarketGroupWithPopularBetBuilders } from '../../../../model/types';
import useDisplayableMarketGroups from '../../hooks/useDisplayableMarketGroups';
import { MarketGroupActionTiles } from '../MarketGroupActionTiles/MarketGroupActionTiles';

import {
  TXTCLI_Clear,
  TXTWCL_Find_market_by_nameXX,
  TXTWCL_No_results_found_for_N0,
  TXTWCL_There_are_no_markets_available_ELL
} from './MarketGroups.messages.json';
import styles from './MarketGroups.scss';
import { useGetTemplatesForGroup } from './hooks/useGetTemplatesForGroup';

const numberOfInitiallyOpenMarkets = 5;

const isMarketGroupWithPopularBetBuilders = (
  group: ClientMarketGroup
): group is MarketGroupWithPopularBetBuilders => 'popularBetBuilders' in group;

const Markets: React.FC = () => {
  const { widgetSelect: marketsWidgetSelect, widgetDispatch } = useSportsEventPageWidget();
  const { widgetSelect } = useWidget();
  const { formatMessage } = useRegulatoryIntl();

  const { eventTypeSlug, eventTypeId, competitionSlug, competitionId, eventSlug, eventId } =
    useSelector(select.sports.getUrlParams);

  const config = {
    eventTypeSlug,
    eventTypeId,
    competitionSlug,
    competitionId,
    eventSlug,
    eventId
  };

  const markets = useSelector(marketsWidgetSelect.markets) as Record<MarketId, MarketView>;
  const marketGroups = useSelector(marketsWidgetSelect.marketsFeature);
  const locale = useSelector(select.locale.get);
  const hasInPlayMarkets = useSelector(widgetSelect?.hasInPlayMarkets ?? noop);
  const { hash } = useLocation();
  const eventStartTime = !isEmpty(markets) ? Object.values(markets)[0].marketTime : '';

  const [marketGroupSlug, setMarketGroupSlug] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const selectedSubMarketGroupType = useSelector(marketsWidgetSelect.selectedSubMarketGroupType);
  const setSelectedMarketGroupType = useCallback(
    (type: SubMarketGroupType | undefined) => {
      widgetDispatch.setSelectedActionTile(type);
    },
    [widgetDispatch]
  );

  const [displayableMarketGroups, currentGroup] = useDisplayableMarketGroups({
    markets,
    groups: marketGroups,
    currentMarketGroupSlug: marketGroupSlug
  });

  const searchTrackingLabel = useTrackingLabel(TXTWCL_Find_market_by_nameXX);

  const eventPageTabEntities: EventPageTabEntities = {
    eventId: Number(eventId),
    sportId: Number(eventTypeId),
    competitionId: Number(competitionId),
    eventStartTime
  };

  useEffect(() => {
    setSearchTerm('');
  }, [marketGroupSlug]);

  useEffect(() => {
    setMarketGroupSlug(hash.replace('#', ''));
  }, [hash]);

  const lowerSearchTerm = searchTerm.toLowerCase();

  const templates = currentGroup
    ? lowerSearchTerm
      ? currentGroup.templates.filter((template) => {
          if (isCompoundMarketTemplate(template)) {
            const marketTitleTranslationTag = marketTemplateTranslationMap[template.config.title];
            const title = marketTitleTranslationTag
              ? formatMessage(marketTitleTranslationTag).toLowerCase()
              : '';
            return title.includes(lowerSearchTerm);
          }
          return markets[template.marketId].marketName.toLowerCase().includes(lowerSearchTerm);
        })
      : currentGroup.templates
    : [];

  const sortedTemplates = useGetTemplatesForGroup({
    currentGroup,
    templates,
    markets,
    locale,
    selectedSubMarketGroupType
  });

  if (!displayableMarketGroups.length) {
    return <Message message={<Translation {...TXTWCL_There_are_no_markets_available_ELL} />} />;
  } else if (!currentGroup) {
    return (
      <Redirect
        alias="sportsEvent"
        values={{
          ...config,
          marketGroup: undefined
        }}
      />
    );
  } else if (!currentGroup.templates.length) {
    return (
      <>
        <MarketGroupTabs
          groups={displayableMarketGroups}
          routeValues={config}
          marketGroupSlug={marketGroupSlug}
          entities={eventPageTabEntities}
        />
        <Message message={<Translation {...TXTWCL_There_are_no_markets_available_ELL} />} />
      </>
    );
  }

  const onSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const onSearchTermClear = () => setSearchTerm('');

  const initiallyOpenedPanels = sortedTemplates
    .slice(0, numberOfInitiallyOpenMarkets)
    .map((template) =>
      isCompoundMarketTemplate(template) ? template.marketIds[0] : template.marketId
    );

  return (
    <>
      <MarketGroupTabs
        groups={displayableMarketGroups}
        routeValues={config}
        marketGroupSlug={marketGroupSlug}
        entities={eventPageTabEntities}
      />

      <MarketGroupActionTiles
        eventTypeId={Number(eventTypeId)}
        marketGroup={currentGroup}
        onTileSelected={setSelectedMarketGroupType}
      />

      {currentGroup.type === 'ALL_MARKETS' && (
        <div className={styles.searchWrapper}>
          <Search
            onChange={onSearchTermChange}
            onClear={onSearchTermClear}
            searchTerm={searchTerm}
            trackingLabel={searchTrackingLabel}
            placeholderTranslation={TXTWCL_Find_market_by_nameXX}
          />
        </div>
      )}

      {sortedTemplates.length ? (
        <>
          {isMarketGroupWithPopularBetBuilders(currentGroup) &&
          !isEmpty(currentGroup.popularBetBuilders) ? (
            <div className={styles.betBuilderWrapper}>
              <PopularBetBuilders
                widgetSelect={{
                  popularBetBuilders: () => currentGroup.popularBetBuilders,
                  hasPopularBetBuilders: () => !isEmpty(currentGroup.popularBetBuilders)
                }}
                isEventPage
              />
            </div>
          ) : null}

          {sortedTemplates.map((template) => {
            const activeMarketId = isCompoundMarketTemplate(template)
              ? template.marketIds[0]
              : template.marketId;

            return (
              <MarketTemplatePanel
                key={`${marketGroupSlug}${selectedSubMarketGroupType}${activeMarketId}`}
                isOpenInitially={initiallyOpenedPanels.includes(activeMarketId)}
                highlight={searchTerm}
                {...template}
              />
            );
          })}

          {hasInPlayMarkets ? <LiveDataWarning /> : null}
        </>
      ) : (
        <div className={styles.afterMarkets}>
          <p className={styles.message}>
            <Translation values={{ 0: searchTerm }} {...TXTWCL_No_results_found_for_N0} />
          </p>

          <div className={styles.buttonWrapper}>
            <Button onClick={onSearchTermClear}>
              <Translation {...TXTCLI_Clear} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default Markets;
